import { STATES } from "../data/states.js";
import { formatDecimal, formatMoney, formatPercent, roundMoney } from "./calc.js";
import {
  applyDealTaxRates,
  derived,
  getAppState,
  resetDealTaxToDefault,
  selectCell,
  setCommissions,
  setJurisdiction,
  setProductProfit,
  setSelectedFormIds,
  startNewQuote,
  updateCustomer,
  updateDealCosts,
  updateDown,
  updateStructure,
  updateTerm,
  updateUi,
} from "./state.js";
import {
  openDealSummaryPdf,
  openQuotePdf,
  previewSnippet,
} from "./print.js";
import {
  defaultSelectedFormIds,
  getFormsForState,
  openDealPacket,
  openFormPrint,
} from "./forms.js";

const PRINT_DISCLAIMER_TEXT =
  "The forms provided are from the Envision DMS Forms Library only — no other vendor. It is the dealership's responsibility to ensure they are using the correct forms. If a form in THIS library is out of date or missing information, please notify us right away at les@envisiondms.com — Be sure to verify tax rates, as they may change. We attempt to keep all items current, but should you notice an incorrect tax rate, or any other discrepancy of any kind, please contact us right away at les@envisiondms.com and/or 888-799-1251.";

/** Pending print action after user clicks I Agree on the disclaimer. */
let pendingPrintAction = null;

function $(sel, root = document) {
  return root.querySelector(sel);
}

function parseNumber(value) {
  if (value == null || value === "") return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Parse while typing; skip incomplete values so caret does not jump. */
function parseNumberLive(value) {
  const raw = String(value ?? "")
    .trim()
    .replace(/,/g, "");
  if (raw === "" || raw === "-" || raw === "." || raw === "-.") return null;
  if (/^-?\d+\.$/.test(raw)) {
    const n = Number(raw.slice(0, -1));
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Live numeric binding: update state on every input so financials recalculate
 * in real time. Panels skip full DOM rebuild while the focused field is edited.
 * Empty/incomplete tokens are ignored while typing; blur/change commits empty
 * as 0 unless emptyPolicy is "keep" (caller restores prior value).
 */
function bindNumber(el, onChange, opts = {}) {
  if (!el || el.dataset.bound === "1") return;
  el.dataset.bound = "1";
  const emptyPolicy = opts.emptyPolicy || "zero";
  const commitLive = () => {
    const n = parseNumberLive(el.value);
    if (n === null) return;
    onChange(n);
  };
  const commitFinal = () => {
    const raw = String(el.value ?? "")
      .trim()
      .replace(/,/g, "");
    if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
      if (emptyPolicy === "keep") {
        onChange(null);
        return;
      }
      onChange(0);
      return;
    }
    const n = parseNumberLive(el.value);
    if (n === null) return;
    onChange(n);
  };
  el.addEventListener("input", commitLive);
  el.addEventListener("change", commitFinal);
  el.addEventListener("blur", commitFinal);
}

/** Skip full panel rebuild while a field inside it is being edited. */
function isEditingInside(host) {
  const ae = document.activeElement;
  return !!(
    ae &&
    host &&
    host.contains(ae) &&
    (ae.tagName === "INPUT" ||
      ae.tagName === "TEXTAREA" ||
      ae.tagName === "SELECT")
  );
}

function syncStructureDisplays(host, state, d) {
  const af = host.querySelector(".total-line strong");
  if (af) af.textContent = formatMoney(d.amountFinanced);
  const mp = host.querySelector('[data-structure-display="monthlyPayment"]');
  if (mp && document.activeElement !== mp) {
    mp.value = formatDecimal(d.structurePayment);
  }
  const moneyKeys = new Set([
    "price",
    "fees",
    "insGap",
    "accessories",
    "vsc",
    "tax",
    "down",
    "trade",
    "buyRate",
  ]);
  const intKeys = new Set(["firstPaymentDays", "frequencyMonths"]);
  host.querySelectorAll("[data-structure]").forEach((input) => {
    if (document.activeElement === input) return;
    const key = input.getAttribute("data-structure");
    if (moneyKeys.has(key)) {
      input.value = formatDecimal(state.structure[key]);
      return;
    }
    if (intKeys.has(key) && state.structure[key] != null) {
      input.value = String(state.structure[key]);
    }
  });
  const freqMeta = host.querySelector("[data-frequency-meta]");
  if (freqMeta) {
    freqMeta.textContent = `${d.frequencyMonths} months @ ${formatPercent(
      state.structure.buyRate
    )} buy rate`;
  }
  const selectedOpt = host.querySelector("[data-selected-option]");
  if (selectedOpt) {
    selectedOpt.textContent = `Selected option: ${formatMoney(d.selectedPayment)} / mo · ${d.selectedTerm.months} mo @ ${formatPercent(d.selectedTerm.apr)}`;
  }
  const customRow = host.querySelector("#customMonthsRow");
  const isCustom = String(state.structure.frequencyPreset) === "custom";
  if (customRow) {
    customRow.hidden = false;
    customRow.classList.toggle("is-open", isCustom);
    const customInput = customRow.querySelector("input");
    if (customInput) {
      customInput.disabled = !isCustom;
      if (isCustom && document.activeElement !== customInput) {
        customInput.value = String(state.structure.frequencyMonths);
      }
    }
  }
  const freqSelect = host.querySelector("[data-frequency-preset]");
  if (freqSelect && document.activeElement !== freqSelect) {
    freqSelect.value = String(state.structure.frequencyPreset || "48");
  }
}

function syncSummaryDisplays(host, state, d) {
  const heroPay = host.querySelector("[data-summary-pay]");
  if (heroPay) {
    heroPay.innerHTML = `${formatMoney(d.selectedPayment)} <em>/ mo</em>`;
  }
  const setText = (sel, value) => {
    const el = host.querySelector(sel);
    if (el) el.textContent = value;
  };
  setText("[data-summary-af]", formatMoney(d.selectedAmountFinanced));
  setText("[data-summary-fc]", formatMoney(d.financeCharge));
  setText("[data-summary-down]", formatMoney(d.selectedDown));
  setText("[data-summary-term]", String(d.selectedTerm.months));
  setText("[data-summary-apr]", formatPercent(d.selectedTerm.apr));
  setText("[data-summary-costs]", formatMoney(d.dealCostsTotal));
  setText("[data-summary-front]", formatMoney(d.frontGross));
  setText("[data-summary-back]", formatMoney(d.backGross));
  setText("[data-summary-total-gross]", formatMoney(d.totalGross));
  setText("[data-summary-net]", formatMoney(d.netProfit));
  host.querySelectorAll("[data-cost]").forEach((input) => {
    if (document.activeElement === input) return;
    const key = input.dataset.cost;
    if (key && state.dealCosts[key] != null) {
      input.value = formatDecimal(state.dealCosts[key]);
    }
  });
  const profitInput = host.querySelector("#productProfitInput");
  if (profitInput && document.activeElement !== profitInput) {
    profitInput.value = formatDecimal(state.productProfit);
  }
  const commissionsInput = host.querySelector("#commissionsInput");
  if (commissionsInput && document.activeElement !== commissionsInput) {
    commissionsInput.value = formatDecimal(state.commissions);
  }
}

export function mountApp(root) {
  root.innerHTML = layoutShell();
  wireStatic(root);
  return () => render(root, getAppState());
}

function layoutShell() {
  return `
    <div class="app-wash" aria-hidden="true"></div>
    <header class="site-header">
      <div class="brand-block">
        <div class="brand-mark" aria-hidden="true">E</div>
        <div>
          <p class="brand-kicker">EnvisionDMS.com</p>
          <h1 class="brand-title">ENVISION</h1>
          <p class="brand-sub">Desking by EnvisionDMS.com</p>
        </div>
      </div>
      <div class="header-controls">
        <label class="state-label">
          <span>Selected State:</span>
          <select id="stateSelect" class="state-select" aria-label="Selected state"></select>
        </label>
        <div class="header-meta" id="taxMeta"></div>
      </div>
    </header>

    <main class="desk-grid">
      <section class="panel structure-panel" aria-labelledby="structureHeading">
        <h2 id="structureHeading">Deal Structure</h2>
        <div id="structureFields" class="field-stack"></div>
      </section>

      <section class="panel options-panel" aria-labelledby="optionsHeading">
        <h2 id="optionsHeading">Payment Options</h2>
        <div id="paymentMatrix" class="payment-matrix"></div>
        <div class="panel-actions">
          <button type="button" class="btn btn-secondary" id="btnPrintQuote">Print Quote</button>
          <button type="button" class="btn btn-primary" id="btnStartDeal">Start Deal</button>
        </div>
      </section>

      <section class="panel summary-panel" aria-labelledby="summaryHeading">
        <div class="summary-head">
          <h2 id="summaryHeading">Deal Summary</h2>
        </div>
        <div id="dealSummary" class="summary-body"></div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="site-footer-row">
        <span>public.envisiondms.com</span>
        <span>Fees &amp; tax defaults reset on each visit. Tax overrides apply to this deal only.</span>
        <button type="button" class="linkish" id="btnNewQuote">New Quote</button>
      </div>
      <p class="site-footer-disclaimer">
        The user agrees that usage of these forms from this library is at their own free will
        and the user should verify their acceptance, that their state recognizes them as legal
        documents, and that they are accepted for use in their state. If a form is inaccurate
        or missing information or contains incorrect data, please email
        <a href="mailto:support@envisiondms.com">support@envisiondms.com</a> immediately.
        Usage of the forms provided here automatically constitutes your acceptance of forms
        as-is, and they are printed by you, at will and at your discretion and choice, as they
        are provided freely. We try to maintain updated forms and appreciate your business.
      </p>
    </footer>

    <div id="modalRoot"></div>
  `;
}

function wireStatic(root) {
  const select = $("#stateSelect", root);
  select.innerHTML = STATES.map(
    (s) => `<option value="${s.code}">${s.code} — ${s.name}</option>`
  ).join("");

  select.addEventListener("change", () => setJurisdiction(select.value));

  $("#btnPrintQuote", root).addEventListener("click", () =>
    updateUi({ printQuoteOpen: true, printVersion: "customer" })
  );
  $("#btnStartDeal", root).addEventListener("click", () => {
    const app = getAppState();
    const needsDefaults =
      !Array.isArray(app.ui.selectedFormIds) ||
      app.ui.formsSelectionState !== app.stateCode;
    updateUi({
      formsPackOpen: true,
      ...(needsDefaults
        ? {
            selectedFormIds: defaultSelectedFormIds(app.stateCode),
            formsSelectionState: app.stateCode,
          }
        : {}),
    });
  });
  $("#btnNewQuote", root)?.addEventListener("click", () => {
    if (
      window.confirm(
        "Start a new quote? Deal Structure, tax overrides, and selections reset to defaults for this visit."
      )
    ) {
      startNewQuote();
    }
  });
  // Print Summary / Total Tax controls are re-rendered inside panels
  root.addEventListener("click", (e) => {
    const printBtn = e.target.closest("#btnPrintSummary");
    if (printBtn) {
      updateUi({ printSummaryOpen: true, printVersion: "customer" });
      return;
    }
    const taxBtn = e.target.closest("#btnEditTax");
    if (taxBtn) {
      updateUi({ taxRatesOpen: true });
    }
  });
}

export function render(root, state) {
  const d = derived(state);
  const select = $("#stateSelect", root);
  if (select && select.value !== state.stateCode) select.value = state.stateCode;
  const modalOpen =
    state.ui.formsPackOpen ||
    state.ui.taxRatesOpen ||
    state.ui.printQuoteOpen ||
    state.ui.printSummaryOpen ||
    state.ui.printDisclaimerOpen;
  if (select) select.disabled = !!modalOpen;
  // Keep worksheet out of tab order / edits while a modal is open (preserves tax drafts).
  for (const sel of [".site-header", ".desk-grid", ".site-footer"]) {
    const el = $(sel, root);
    if (el) {
      if (modalOpen) el.setAttribute("inert", "");
      else el.removeAttribute("inert");
    }
  }

  const taxMeta = d.taxManual
    ? `Tax ${formatPercent(d.taxRatePercent)} (manual) · Default fees ${formatMoney(d.jurisdiction.defaultFees)}`
    : `Tax ${formatPercent(d.jurisdiction.taxRate * 100)} · Default fees ${formatMoney(d.jurisdiction.defaultFees)}`;
  $("#taxMeta", root).textContent = taxMeta;

  renderStructure(root, state, d);
  renderMatrix(root, state, d);
  renderSummary(root, state, d);
  renderModals(root, state, d);
}

function structureRowsSpec() {
  // Order: Price → Ins/GAP/VSI → Fees → Products/VSC → Accessories → Trade In → rest
  return [
    { key: "price", label: "Price", sign: "+", editable: true },
    { key: "insGap", label: "Ins / GAP / VSI", sign: "+", editable: true },
    { key: "fees", label: "Fees", sign: "+", editable: true },
    { key: "vsc", label: "Products / VSC", sign: "+", editable: true },
    { key: "accessories", label: "Accessories", sign: "+", editable: true },
    { key: "trade", label: "Trade In", sign: "−", editable: true },
    { key: "tax", label: "Total Tax", sign: "+", editable: false, taxButton: true },
    { key: "down", label: "Down Payment", sign: "−", editable: true },
  ];
}

const FREQUENCY_PRESETS = ["12", "24", "36", "48"];

function renderStructure(root, state, d) {
  const host = $("#structureFields", root);
  if (isEditingInside(host)) {
    syncStructureDisplays(host, state, d);
    return;
  }
  const s = state.structure;
  const preset = String(s.frequencyPreset || "48");
  const isCustom = preset === "custom";
  const rows = structureRowsSpec()
    .map((row) => {
      if (row.taxButton) {
        const manual = s.taxManual
          ? ' <em class="lock">manual</em>'
          : ' <em class="lock">edit</em>';
        return `
          <div class="field-row is-locked tax-row">
            <span class="field-label">
              <i class="sign">${row.sign}</i>
              <button type="button" class="tax-edit-btn" id="btnEditTax" title="Enter tax rates for this deal">
                ${row.label}
              </button>${manual}
            </span>
            <input type="number" step="0.01" inputmode="decimal" data-structure="tax" value="${formatDecimal(s.tax)}" readonly tabindex="-1" aria-label="Total tax amount" />
          </div>
        `;
      }
      return `
        <label class="field-row">
          <span class="field-label"><i class="sign">${row.sign}</i>${row.label}</span>
          <input type="number" step="0.01" inputmode="decimal" data-structure="${row.key}" value="${formatDecimal(s[row.key])}" />
        </label>
      `;
    })
    .join("");

  const freqOptions = FREQUENCY_PRESETS.map(
    (m) =>
      `<option value="${m}" ${preset === m ? "selected" : ""}>${m} months</option>`
  ).join("");

  host.innerHTML = `
    ${rows}
    <div class="structure-totals">
      <div class="total-line"><span>Amount Financed</span><strong>${formatMoney(d.amountFinanced)}</strong></div>
      <label class="field-row">
        <span class="field-label">Frequency</span>
        <select id="frequencyPreset" data-frequency-preset aria-label="Payment frequency in months">
          ${freqOptions}
          <option value="custom" ${isCustom ? "selected" : ""}>CUSTOM</option>
        </select>
      </label>
      <label class="field-row custom-months-row ${isCustom ? "is-open" : ""}" id="customMonthsRow">
        <span class="field-label">Custom months</span>
        <input type="number" min="1" step="1" inputmode="numeric" data-structure="frequencyMonths" value="${s.frequencyMonths}" ${isCustom ? "" : "disabled"} />
      </label>
      <p class="freq-meta" data-frequency-meta>${d.frequencyMonths} months @ ${formatPercent(s.buyRate)} buy rate</p>
      <label class="field-row">
        <span class="field-label">Buy Rate (%)</span>
        <input type="number" step="0.01" inputmode="decimal" data-structure="buyRate" value="${formatDecimal(s.buyRate)}" />
      </label>
      <label class="field-row">
        <span class="field-label">Buy Rate Payment</span>
        <input type="number" step="0.01" readonly data-structure-display="monthlyPayment" value="${formatDecimal(d.structurePayment)}" aria-label="Payment at buy rate and frequency" />
      </label>
      <p class="freq-meta" data-selected-option>Selected option: ${formatMoney(d.selectedPayment)} / mo · ${d.selectedTerm.months} mo @ ${formatPercent(d.selectedTerm.apr)}</p>
      <label class="field-row">
        <span class="field-label">First Payment (days)</span>
        <input type="number" step="1" inputmode="numeric" data-structure="firstPaymentDays" value="${s.firstPaymentDays}" />
      </label>
    </div>
  `;

  const freqSelect = host.querySelector("[data-frequency-preset]");
  const applyFrequency = () => {
    const value = freqSelect.value;
    const isCustom = value === "custom";
    // Open custom months immediately (before state emit / sync).
    const customRow = host.querySelector("#customMonthsRow");
    const customInput = customRow?.querySelector("input");
    if (customRow) {
      customRow.classList.toggle("is-open", isCustom);
      if (customInput) customInput.disabled = !isCustom;
    }
    updateStructure({ frequencyPreset: value });
    if (isCustom) {
      requestAnimationFrame(() => {
        const input = host.querySelector("#customMonthsRow input");
        if (!input) return;
        input.disabled = false;
        input.focus();
        input.select();
      });
    }
  };
  freqSelect?.addEventListener("change", applyFrequency);

  host.querySelectorAll("[data-structure]").forEach((input) => {
    bindNumber(input, (val) => {
      const key = input.getAttribute("data-structure");
      if (key === "tax") return;
      // Custom months must stay ≥ 1; restore display if cleared/zeroed without emitting.
      if (key === "frequencyMonths" && val < 1) {
        input.value = String(getAppState().structure.frequencyMonths);
        return;
      }
      const moneyKeys = new Set([
        "price",
        "fees",
        "insGap",
        "accessories",
        "vsc",
        "down",
        "trade",
        "buyRate",
      ]);
      updateStructure({ [key]: moneyKeys.has(key) ? roundMoney(val) : val });
    });
  });
}

function renderMatrix(root, state, d) {
  const host = $("#paymentMatrix", root);
  if (isEditingInside(host)) {
    host.querySelectorAll(".pay-cell").forEach((btn) => {
      const di = Number(btn.dataset.di);
      const ti = Number(btn.dataset.ti);
      const cell = d.matrix[di]?.[ti];
      if (!cell) return;
      btn.textContent = formatMoney(cell.payment);
      btn.classList.toggle(
        "is-selected",
        state.selection.downIndex === di && state.selection.termIndex === ti
      );
    });
    host.querySelectorAll("[data-down]").forEach((input) => {
      if (document.activeElement === input) return;
      const i = Number(input.dataset.down);
      if (Number.isFinite(i) && state.downs[i] != null) {
        input.value = formatDecimal(state.downs[i]);
      }
    });
    host.querySelectorAll("[data-term-apr]").forEach((input) => {
      if (document.activeElement === input) return;
      const i = Number(input.dataset.termApr);
      if (Number.isFinite(i) && state.terms[i]) {
        input.value = formatDecimal(state.terms[i].apr);
      }
    });
    host.querySelectorAll("[data-term-months]").forEach((input) => {
      if (document.activeElement === input) return;
      const i = Number(input.dataset.termMonths);
      if (Number.isFinite(i) && state.terms[i]) {
        input.value = String(state.terms[i].months);
      }
    });
    return;
  }
  const downHeaders = state.downs
    .map(
      (down, i) => `
      <th>
        <span class="th-label">Down</span>
        <input type="number" step="0.01" inputmode="decimal" data-down="${i}" value="${formatDecimal(down)}" aria-label="Down column ${i + 1}" />
      </th>`
    )
    .join("");

  const body = state.terms
    .map((term, ti) => {
      const cells = state.downs
        .map((_, di) => {
          const cell = d.matrix[di][ti];
          const selected =
            state.selection.downIndex === di && state.selection.termIndex === ti;
          return `
            <td>
              <button type="button" class="pay-cell ${selected ? "is-selected" : ""}" data-di="${di}" data-ti="${ti}">
                ${formatMoney(cell.payment)}
              </button>
            </td>`;
        })
        .join("");
      return `
        <tr>
          <th class="term-cell">
            <label>
              <span>Term</span>
              <input type="number" step="1" data-term-months="${ti}" value="${term.months}" />
            </label>
            <label>
              <span>APR %</span>
              <input type="number" step="0.01" inputmode="decimal" data-term-apr="${ti}" value="${formatDecimal(term.apr)}" />
            </label>
          </th>
          ${cells}
        </tr>`;
    })
    .join("");

  host.innerHTML = `
    <table class="matrix-table">
      <thead>
        <tr>
          <th class="term-head">Term / APR</th>
          ${downHeaders}
        </tr>
      </thead>
      <tbody>${body}</tbody>
    </table>
  `;

  host.querySelectorAll("[data-down]").forEach((input) => {
    bindNumber(
      input,
      (val) => {
        const i = Number(input.dataset.down);
        if (val == null) {
          const prior = getAppState().downs[i];
          if (prior != null) input.value = formatDecimal(prior);
          return;
        }
        updateDown(i, roundMoney(val));
      },
      { emptyPolicy: "keep" }
    );
  });
  host.querySelectorAll("[data-term-months]").forEach((input) => {
    bindNumber(input, (val) => {
      const i = Number(input.dataset.termMonths);
      // Clearing Term must not force a 1-month loan; restore prior term instead.
      if (val == null || val < 1) {
        const prior = getAppState().terms[i]?.months;
        if (prior != null) input.value = String(prior);
        return;
      }
      updateTerm(i, { months: Math.round(val) });
    }, { emptyPolicy: "keep" });
  });
  host.querySelectorAll("[data-term-apr]").forEach((input) => {
    bindNumber(input, (val) => {
      const i = Number(input.dataset.termApr);
      if (val == null) {
        const prior = getAppState().terms[i]?.apr;
        if (prior != null) input.value = formatDecimal(prior);
        return;
      }
      updateTerm(i, { apr: roundMoney(val) });
    }, { emptyPolicy: "keep" });
  });
  host.querySelectorAll(".pay-cell").forEach((btn) => {
    btn.addEventListener("click", () =>
      selectCell(Number(btn.dataset.di), Number(btn.dataset.ti))
    );
  });
}

function renderSummary(root, state, d) {
  const host = $("#dealSummary", root);
  if (isEditingInside(host)) {
    syncSummaryDisplays(host, state, d);
    return;
  }
  host.innerHTML = `
    <div class="summary-hero">
      <div><span>Down</span><strong data-summary-down>${formatMoney(d.selectedDown)}</strong></div>
      <div><span>Term</span><strong data-summary-term>${d.selectedTerm.months}</strong></div>
      <div><span>APR</span><strong data-summary-apr>${formatPercent(d.selectedTerm.apr)}</strong></div>
      <div><span>Payment</span><strong class="emphasis" data-summary-pay>${formatMoney(d.selectedPayment)} <em>/ mo</em></strong></div>
    </div>
    <dl class="summary-list">
      <div><dt>Amount Financed</dt><dd data-summary-af>${formatMoney(d.selectedAmountFinanced)}</dd></div>
      <div><dt>Finance Charge</dt><dd data-summary-fc>${formatMoney(d.financeCharge)}</dd></div>
    </dl>

    <div class="summary-block">
      <div class="summary-block-head">
        <h3>Deal Costs</h3>
        <button type="button" class="linkish" id="btnDealCosts">${state.ui.dealCostsOpen ? "Close" : "Menu"}</button>
      </div>
      <p class="costs-total" data-summary-costs>${formatMoney(d.dealCostsTotal)}</p>
      <div class="deal-costs-menu ${state.ui.dealCostsOpen ? "is-open" : ""}" id="dealCostsMenu">
        <label>Vehicle Cost<input type="number" step="0.01" inputmode="decimal" data-cost="vehicleCost" value="${formatDecimal(state.dealCosts.vehicleCost)}" /></label>
        <label>Fee Cost<input type="number" step="0.01" inputmode="decimal" data-cost="feeCost" value="${formatDecimal(state.dealCosts.feeCost)}" /></label>
        <label>Product Cost<input type="number" step="0.01" inputmode="decimal" data-cost="productCost" value="${formatDecimal(state.dealCosts.productCost)}" /></label>
        <label>Back Gross (editable)<input type="number" step="0.01" inputmode="decimal" id="productProfitInput" value="${formatDecimal(state.productProfit)}" /></label>
      </div>
    </div>

    <dl class="summary-list gross-list">
      <div><dt>Front Gross</dt><dd data-summary-front>${formatMoney(d.frontGross)}</dd></div>
      <div><dt>Back Gross</dt><dd data-summary-back>${formatMoney(d.backGross)}</dd></div>
      <div><dt>Total Gross</dt><dd data-summary-total-gross>${formatMoney(d.totalGross)}</dd></div>
      <div>
        <dt>Commissions</dt>
        <dd><input type="number" step="0.01" inputmode="decimal" id="commissionsInput" value="${formatDecimal(state.commissions)}" /></dd>
      </div>
      <div><dt>Net Profit</dt><dd class="emphasis" data-summary-net>${formatMoney(d.netProfit)}</dd></div>
    </dl>

    <div class="customer-mini">
      <h3>Customer / Vehicle</h3>
      <div class="mini-grid">
        <input type="text" placeholder="Customer name" data-customer="name" value="${escapeAttr(state.customer.name)}" />
        <input type="text" placeholder="Stock #" data-customer="stock" value="${escapeAttr(state.customer.stock)}" />
        <input type="text" placeholder="Year" data-customer="year" value="${escapeAttr(state.customer.year)}" />
        <input type="text" placeholder="Make" data-customer="make" value="${escapeAttr(state.customer.make)}" />
        <input type="text" placeholder="Model" data-customer="model" value="${escapeAttr(state.customer.model)}" />
        <input type="text" placeholder="VIN" data-customer="vin" value="${escapeAttr(state.customer.vin)}" />
        <input type="text" placeholder="Color" data-customer="color" value="${escapeAttr(state.customer.color || "")}" />
        <input type="text" placeholder="Body" data-customer="body" value="${escapeAttr(state.customer.body || "")}" />
        <input type="text" placeholder="Odometer" data-customer="mileage" inputmode="numeric" value="${escapeAttr(state.customer.mileage || "")}" />
      </div>
    </div>

    <div class="summary-print-row">
      <button type="button" class="icon-btn" id="btnPrintSummary" title="Print Deal Summary" aria-label="Print Deal Summary">
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M6 9V3h12v6h-2V5H8v4H6zm0 2h12a3 3 0 013 3v5h-4v4H7v-4H3v-5a3 3 0 013-3zm2 7h8v-3H8v3zm-3-5a1 1 0 100 2 1 1 0 000-2z"/></svg>
      </button>
      <span class="summary-print-label">Print Deal Summary</span>
    </div>
  `;

  $("#btnDealCosts", host)?.addEventListener("click", () =>
    updateUi({ dealCostsOpen: !state.ui.dealCostsOpen })
  );

  host.querySelectorAll("[data-cost]").forEach((input) => {
    bindNumber(input, (val) =>
      updateDealCosts({ [input.dataset.cost]: roundMoney(val) })
    );
  });
  bindNumber($("#productProfitInput", host), (val) =>
    setProductProfit(roundMoney(val))
  );
  bindNumber($("#commissionsInput", host), (val) =>
    setCommissions(roundMoney(val))
  );

  host.querySelectorAll("[data-customer]").forEach((input) => {
    if (input.dataset.bound === "1") return;
    input.dataset.bound = "1";
    const commit = () =>
      updateCustomer({ [input.dataset.customer]: input.value });
    input.addEventListener("input", commit);
    input.addEventListener("change", commit);
  });
}

/** Read latest customer/vehicle text from the DOM (no emit — safe before window.open). */
function readCustomerPatch(root) {
  const host = $("#dealSummary", root) || root;
  const patch = {};
  if (!host) return patch;
  host.querySelectorAll("[data-customer]").forEach((input) => {
    const key = input.dataset.customer;
    if (key) patch[key] = input.value;
  });
  return patch;
}

function stateWithDomCustomer(root = document.getElementById("app") || document) {
  const app = getAppState();
  const patch = readCustomerPatch(root);
  return {
    ...app,
    customer: { ...app.customer, ...patch },
    _customerPatch: patch,
  };
}

function escapeAttr(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function printOptionsHtml(state, kind) {
  const opts = state.ui;
  const isDealership = opts.printVersion === "dealership";
  const isQuote = kind === "quote";
  const template = opts.printTemplate === "grid" ? "grid" : "classic";
  const preview = previewSnippet(state, kind, {
    version: opts.printVersion,
    showCost: opts.showCost,
    showFrontGross: opts.showFrontGross,
    showBackGross: opts.showBackGross,
    showNetProfit: opts.showNetProfit,
    showTradeCredit: opts.showTradeCredit !== false,
    showApr: opts.showApr !== false,
    showFinanceCharge: opts.showFinanceCharge !== false,
    showAmountFinanced: opts.showAmountFinanced !== false,
    showDealStructure: opts.showDealStructure !== false,
    template,
  });
  const title = kind === "summary" ? "Print Deal Summary" : "Print Quote";

  return `
    <div class="modal-backdrop" data-close="1">
      <div class="modal modal-wide" role="dialog" aria-modal="true" aria-label="${title}">
        <header class="modal-head">
          <h2>${title}</h2>
          <button type="button" class="icon-btn" data-close="1" aria-label="Close">×</button>
        </header>
        <div class="modal-body">
          <fieldset class="version-fieldset">
            <legend>Version</legend>
            <label class="radio"><input type="radio" name="printVersion" value="customer" ${!isDealership ? "checked" : ""} /> Customer Version</label>
            <label class="radio"><input type="radio" name="printVersion" value="dealership" ${isDealership ? "checked" : ""} /> Dealership Version</label>
          </fieldset>

          ${
            isQuote
              ? `<fieldset class="version-fieldset">
            <legend>Quote Template</legend>
            <label class="radio"><input type="radio" name="printTemplate" value="classic" ${template === "classic" ? "checked" : ""} /> Classic — single-column payment quote</label>
            <label class="radio"><input type="radio" name="printTemplate" value="grid" ${template === "grid" ? "checked" : ""} /> Grid — payment cards + deal structure</label>
          </fieldset>
          <fieldset class="show-fieldset">
            <legend>Include on Quote</legend>
            <label><input type="checkbox" data-show="showTradeCredit" ${opts.showTradeCredit !== false ? "checked" : ""} /> Trade Credit</label>
            <label><input type="checkbox" data-show="showApr" ${opts.showApr !== false ? "checked" : ""} /> APR</label>
            <label><input type="checkbox" data-show="showFinanceCharge" ${opts.showFinanceCharge !== false ? "checked" : ""} /> Finance Charge</label>
            <label><input type="checkbox" data-show="showAmountFinanced" ${opts.showAmountFinanced !== false ? "checked" : ""} /> Amount Financed</label>
            <label><input type="checkbox" data-show="showDealStructure" ${opts.showDealStructure !== false ? "checked" : ""} /> Deal Structure lines</label>
          </fieldset>`
              : ""
          }

          <fieldset class="show-fieldset ${isDealership ? "" : "is-disabled"}" data-dealer-opts="1" ${isDealership ? "" : "disabled"}>
            <legend>Show (Dealership Version)</legend>
            <label><input type="checkbox" data-show="showCost" ${opts.showCost ? "checked" : ""} /> Cost</label>
            <label><input type="checkbox" data-show="showFrontGross" ${opts.showFrontGross ? "checked" : ""} /> Front Gross</label>
            <label><input type="checkbox" data-show="showBackGross" ${opts.showBackGross ? "checked" : ""} /> Back Gross</label>
            <label><input type="checkbox" data-show="showNetProfit" ${opts.showNetProfit ? "checked" : ""} /> Net Profit</label>
          </fieldset>

          <div class="preview-block">
            <button type="button" class="btn btn-secondary" id="btnClickPreview">Click To Preview</button>
            <div class="preview-thumb" id="previewThumb">${preview}</div>
          </div>
        </div>
        <footer class="modal-foot">
          <button type="button" class="btn btn-ghost" data-close="1">Cancel</button>
          <button type="button" class="btn btn-primary" id="btnPrintNext">Preview / Print</button>
        </footer>
      </div>
    </div>
  `;
}

function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function printDisclaimerHtml() {
  return `
    <div class="modal-backdrop" data-close-disclaimer="1">
      <div class="modal" role="dialog" aria-modal="true" aria-label="Forms library notice">
        <header class="modal-head">
          <h2>Forms Library Notice</h2>
          <button type="button" class="icon-btn" data-close-disclaimer="1" aria-label="Close">×</button>
        </header>
        <div class="modal-body">
          <p class="modal-lead">${escapeHtml(PRINT_DISCLAIMER_TEXT)}</p>
          <p class="modal-fine">You must agree before any quote, deal summary, form, or deal packet can print.</p>
        </div>
        <footer class="modal-foot">
          <button type="button" class="btn btn-ghost" data-close-disclaimer="1">Cancel</button>
          <button type="button" class="btn btn-primary" id="btnDisclaimerAgree">I Agree</button>
        </footer>
      </div>
    </div>
  `;
}

function taxRatesHtml(state, d) {
  const defaultPct = (d.jurisdiction.taxRate * 100).toFixed(2);
  const statePct = state.structure.taxManual
    ? Number(state.structure.customStateTaxRate ?? defaultPct)
    : Number(defaultPct);
  const localPct = state.structure.taxManual
    ? Number(state.structure.customLocalTaxRate || 0)
    : 0;
  const combined = statePct + localPct;

  return `
    <div class="modal-backdrop" data-close="1">
      <div class="modal" role="dialog" aria-modal="true" aria-label="Edit tax rates">
        <header class="modal-head">
          <h2>Total Tax · ${escapeAttr(d.jurisdiction.code)}</h2>
          <button type="button" class="icon-btn" data-close="1" aria-label="Close">×</button>
        </header>
        <div class="modal-body">
          <p class="modal-lead">
            Enter the tax rates for this deal if the default ${escapeAttr(d.jurisdiction.name)} rate
            (${defaultPct}%) is incorrect. Rates apply only to this quote/deal and reset on New Quote or when you leave the site.
          </p>
          <div class="tax-rate-grid">
            <label>
              <span>State / base tax rate (%)</span>
              <input type="number" step="0.01" min="0" inputmode="decimal" id="taxStateRate" value="${formatDecimal(statePct)}" />
            </label>
            <label>
              <span>Local / county tax rate (%)</span>
              <input type="number" step="0.01" min="0" inputmode="decimal" id="taxLocalRate" value="${formatDecimal(localPct)}" />
            </label>
          </div>
          <dl class="tax-rate-preview">
            <div><dt>Combined rate</dt><dd id="taxCombinedPreview">${formatPercent(combined)}</dd></div>
            <div><dt>Taxable base</dt><dd>${formatMoney(d.taxableBaseAmount)}</dd></div>
            <div><dt>Total Tax</dt><dd id="taxAmountPreview">${formatMoney(state.structure.tax)}</dd></div>
          </dl>
          <p class="modal-fine">Printed paperwork will include: <em>*This tax rate has been manually entered</em></p>
        </div>
        <footer class="modal-foot">
          <button type="button" class="btn btn-ghost" id="btnTaxUseDefault">Use State Default</button>
          <button type="button" class="btn btn-ghost" data-close="1">Cancel</button>
          <button type="button" class="btn btn-primary" id="btnTaxApply">Apply to Deal</button>
        </footer>
      </div>
    </div>
  `;
}

function resolvedSelectedFormIds(state) {
  if (Array.isArray(state.ui.selectedFormIds)) return state.ui.selectedFormIds;
  return defaultSelectedFormIds(state.stateCode);
}

function formsPackHtml(state) {
  const forms = getFormsForState(state.stateCode);
  const selected = new Set(resolvedSelectedFormIds(state));
  const selectedCount = forms.filter((f) => selected.has(f.id)).length;
  const d = derived(state);
  const list = forms
    .map((f) => {
      const checked = selected.has(f.id) ? "checked" : "";
      const haystack = `${f.name} ${f.formType} ${f.category} ${f.id}`.toLowerCase();
      return `
        <div class="form-check ${checked ? "is-selected" : ""}" data-form-row data-search="${escapeAttr(haystack)}">
          <label class="form-check-main">
            <input type="checkbox" value="${escapeAttr(f.id)}" data-form-id="${escapeAttr(f.id)}" ${checked} />
            <span>
              <strong>${escapeAttr(f.name)}</strong>
              <em>${escapeAttr(f.formType)} · ${escapeAttr(f.category)}</em>
            </span>
          </label>
          <button type="button" class="btn btn-ghost btn-compact form-preview-btn" data-preview-form="${escapeAttr(f.id)}" title="Preview this form">Preview</button>
        </div>`;
    })
    .join("");

  return `
    <div class="modal-backdrop" data-close="1">
      <div class="modal modal-wide" role="dialog" aria-modal="true" aria-label="Start Deal forms pack">
        <header class="modal-head">
          <h2>Start Deal · ${state.stateCode} Forms Pack</h2>
          <button type="button" class="icon-btn" data-close="1" aria-label="Close">×</button>
        </header>
        <div class="modal-body">
          <p class="modal-lead">
            Search and select ${escapeAttr(d.jurisdiction.name)} forms from the <strong>Envision Forms Library only</strong>
            (no other vendor). Figures use this deal’s live totals
            (AF ${formatMoney(d.selectedAmountFinanced)}, payment ${formatMoney(d.selectedPayment)}).
            Preview any form or the whole pack, then print — you can come back and re-print anytime.
          </p>
          <div class="forms-search-row">
            <input type="search" id="formsSearch" class="forms-search" placeholder="Search forms (e.g. odometer, RISC, as-is)…" autocomplete="off" aria-label="Search forms" />
            <span class="forms-filter-meta" id="formsFilterMeta">${forms.length} forms</span>
          </div>
          <div class="forms-toolbar">
            <div class="forms-count"><strong id="formsSelectedCount">${selectedCount}</strong> selected</div>
            <div class="forms-toolbar-actions">
              <button type="button" class="btn btn-ghost btn-compact" id="btnFormsPriority">Core pack</button>
              <button type="button" class="btn btn-ghost btn-compact" id="btnFormsVisible">Select shown</button>
              <button type="button" class="btn btn-ghost btn-compact" id="btnFormsAll">Select all</button>
              <button type="button" class="btn btn-ghost btn-compact" id="btnFormsClear">Clear</button>
            </div>
          </div>
          <div class="forms-list" id="formsList">${list || "<p>No forms found for this state.</p>"}</div>
          <p class="forms-empty" id="formsEmpty" hidden>No forms match your search.</p>
        </div>
        <footer class="modal-foot forms-foot">
          <button type="button" class="btn btn-ghost" id="btnFormsDone" data-close="1">Done</button>
          <button type="button" class="btn btn-secondary" id="btnPreviewPacket">Preview Selected</button>
          <button type="button" class="btn btn-primary" id="btnPrintPacket">Print Selected</button>
        </footer>
      </div>
    </div>
  `;
}

function renderModals(root, state, d) {
  const host = $("#modalRoot", root);
  if (state.ui.printDisclaimerOpen) {
    if (host.querySelector("#btnDisclaimerAgree")) return;
    host.innerHTML = printDisclaimerHtml();
    wireDisclaimerModal(host);
    return;
  }
  if (state.ui.taxRatesOpen) {
    // Do not rebuild an open tax modal — preserves uncommitted rate drafts.
    if (host.querySelector("#taxStateRate")) return;
    host.innerHTML = taxRatesHtml(state, d || derived(state));
    wireTaxRatesModal(host);
    return;
  }
  if (state.ui.printQuoteOpen) {
    if (
      host.getAttribute("data-print-wired") === "quote" &&
      host.querySelector("#btnPrintNext")
    ) {
      syncPrintModal(host, state, "quote");
      return;
    }
    host.innerHTML = printOptionsHtml(state, "quote");
    wirePrintModal(host, state, "quote");
    host.setAttribute("data-print-wired", "quote");
    return;
  }
  if (state.ui.printSummaryOpen) {
    if (
      host.getAttribute("data-print-wired") === "summary" &&
      host.querySelector("#btnPrintNext")
    ) {
      syncPrintModal(host, state, "summary");
      return;
    }
    host.innerHTML = printOptionsHtml(state, "summary");
    wirePrintModal(host, state, "summary");
    host.setAttribute("data-print-wired", "summary");
    return;
  }
  if (state.ui.formsPackOpen) {
    // Preserve checkbox focus while toggling; rebuild if state changed
    const wiredState = host.getAttribute("data-forms-state");
    if (
      host.hasAttribute("data-forms-wired") &&
      host.querySelector(".forms-list") &&
      wiredState === state.stateCode
    ) {
      syncFormsModalSelection(host, state);
      return;
    }
    host.innerHTML = formsPackHtml(state);
    wireFormsModal(host, state);
    host.setAttribute("data-forms-state", state.stateCode);
    return;
  }
  host.innerHTML = "";
  host.removeAttribute("data-forms-wired");
  host.removeAttribute("data-forms-state");
  host.removeAttribute("data-print-wired");
}

function closeModals() {
  pendingPrintAction = null;
  updateUi({
    printQuoteOpen: false,
    printSummaryOpen: false,
    formsPackOpen: false,
    taxRatesOpen: false,
    printDisclaimerOpen: false,
  });
}

function requestPrintWithDisclaimer(actionFn) {
  // Flush customer/vehicle fields into app state before the disclaimer modal
  // re-renders the page (Color / Body / Odometer must print).
  const patch = readCustomerPatch(document.getElementById("app") || document);
  if (Object.keys(patch).length) updateCustomer(patch);
  pendingPrintAction = actionFn;
  updateUi({ printDisclaimerOpen: true });
}

function wireDisclaimerModal(host) {
  host.querySelectorAll("[data-close-disclaimer]").forEach((el) => {
    el.addEventListener("click", (e) => {
      if (el.classList.contains("modal-backdrop") && e.target !== el) return;
      pendingPrintAction = null;
      updateUi({ printDisclaimerOpen: false });
    });
  });
  $("#btnDisclaimerAgree", host)?.addEventListener("click", () => {
    const action = pendingPrintAction;
    pendingPrintAction = null;
    // Fresh DOM flush on agree so Color / Body / Odometer always print.
    const printState = stateWithDomCustomer();
    if (Object.keys(printState._customerPatch || {}).length) {
      updateCustomer(printState._customerPatch);
    }
    // Close print-options + disclaimer together so Agree does not remount/flicker
    // the quote/summary modal under the print window.
    updateUi({
      printDisclaimerOpen: false,
      printQuoteOpen: false,
      printSummaryOpen: false,
    });
    // Run in this click turn so pop-ups / print stay allowed on mobile Safari.
    if (typeof action === "function") action(printState);
  });
}

function parseTaxRateField(el) {
  const raw = String(el?.value ?? "")
    .trim()
    .replace(/,/g, "");
  if (raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

/** Resolve modal rates so blank fields never silently zero the deal tax. */
function resolveTaxModalRates(stateInput, localInput, defaultPct) {
  const stateRaw = parseTaxRateField(stateInput);
  const localRaw = parseTaxRateField(localInput);
  const fallback = Math.max(0, Number(defaultPct) || 0);
  // Both blank, or blank state with local left at 0 → jurisdiction default.
  if (
    (stateRaw === null && localRaw === null) ||
    (stateRaw === null && (localRaw === null || localRaw === 0))
  ) {
    return { mode: "default", statePct: fallback, localPct: 0 };
  }
  return {
    mode: "manual",
    statePct: stateRaw === null ? fallback : stateRaw,
    localPct: localRaw === null ? 0 : localRaw,
  };
}

function wireTaxRatesModal(host) {
  const stateInput = $("#taxStateRate", host);
  const localInput = $("#taxLocalRate", host);
  const combinedEl = $("#taxCombinedPreview", host);
  const amountEl = $("#taxAmountPreview", host);

  const refreshPreview = () => {
    const app = getAppState();
    const d = derived(app);
    const defaultPct = roundMoney(d.jurisdiction.taxRate * 100);
    const { statePct, localPct } = resolveTaxModalRates(
      stateInput,
      localInput,
      defaultPct
    );
    const combined = statePct + localPct;
    const amount = roundPreviewTax(d.taxableBaseAmount, combined / 100);
    if (combinedEl) combinedEl.textContent = formatPercent(combined);
    if (amountEl) amountEl.textContent = formatMoney(amount);
  };

  stateInput?.addEventListener("input", refreshPreview);
  localInput?.addEventListener("input", refreshPreview);
  refreshPreview();

  host.querySelectorAll("[data-close]").forEach((el) => {
    el.addEventListener("click", (e) => {
      // Backdrop: only close on direct click. Buttons: close / cancel only.
      if (el.classList.contains("modal-backdrop")) {
        if (e.target === el) closeModals();
        return;
      }
      if (el.classList.contains("icon-btn") || el.hasAttribute("data-close")) {
        closeModals();
      }
    });
  });

  $("#btnTaxUseDefault", host)?.addEventListener("click", () => {
    resetDealTaxToDefault();
  });

  $("#btnTaxApply", host)?.addEventListener("click", () => {
    const app = getAppState();
    const d = derived(app);
    const defaultPct = roundMoney(d.jurisdiction.taxRate * 100);
    const resolved = resolveTaxModalRates(stateInput, localInput, defaultPct);
    if (resolved.mode === "default") {
      resetDealTaxToDefault();
      return;
    }
    applyDealTaxRates({
      stateRatePercent: resolved.statePct,
      localRatePercent: resolved.localPct,
    });
  });
}

function roundPreviewTax(base, rate) {
  return Math.round((Math.max(0, Number(base) || 0) * (Number(rate) || 0)) * 100) / 100;
}

/** Update print options modal in place — no full rebuild (stops version toggle flicker). */
function syncPrintModal(host, state, kind) {
  const opts = state.ui;
  const isDealership = opts.printVersion === "dealership";
  host.querySelectorAll('input[name="printVersion"]').forEach((input) => {
    input.checked = input.value === opts.printVersion;
  });
  host.querySelectorAll('input[name="printTemplate"]').forEach((input) => {
    input.checked = input.value === (opts.printTemplate || "classic");
  });
  host.querySelectorAll("[data-show]").forEach((input) => {
    const key = input.dataset.show;
    if (!key) return;
    if (Object.prototype.hasOwnProperty.call(opts, key)) {
      input.checked = !!opts[key];
    } else if (
      key === "showTradeCredit" ||
      key === "showApr" ||
      key === "showFinanceCharge" ||
      key === "showAmountFinanced" ||
      key === "showDealStructure"
    ) {
      input.checked = opts[key] !== false;
    }
  });
  const dealerFs = host.querySelector("[data-dealer-opts]");
  if (dealerFs) {
    dealerFs.disabled = !isDealership;
    dealerFs.classList.toggle("is-disabled", !isDealership);
  }
  const thumb = $("#previewThumb", host);
  if (thumb) {
    thumb.innerHTML = previewSnippet(state, kind, {
      version: opts.printVersion,
      showCost: opts.showCost,
      showFrontGross: opts.showFrontGross,
      showBackGross: opts.showBackGross,
      showNetProfit: opts.showNetProfit,
      showTradeCredit: opts.showTradeCredit !== false,
      showApr: opts.showApr !== false,
      showFinanceCharge: opts.showFinanceCharge !== false,
      showAmountFinanced: opts.showAmountFinanced !== false,
      showDealStructure: opts.showDealStructure !== false,
      template: opts.printTemplate,
    });
  }
}

function wirePrintModal(host, state, kind) {
  host.querySelectorAll("[data-close]").forEach((el) => {
    el.addEventListener("click", (e) => {
      if (el.classList.contains("modal-backdrop")) {
        if (e.target === el) closeModals();
        return;
      }
      if (el.classList.contains("icon-btn") || el.hasAttribute("data-close")) {
        closeModals();
      }
    });
  });

  host.querySelectorAll('input[name="printVersion"]').forEach((input) => {
    input.addEventListener("change", () => {
      // Local DOM sync first so the UI never blinks waiting on a full modal rebuild.
      const dealerFs = host.querySelector("[data-dealer-opts]");
      const isDealership = input.value === "dealership";
      if (dealerFs) {
        dealerFs.disabled = !isDealership;
        dealerFs.classList.toggle("is-disabled", !isDealership);
      }
      updateUi({ printVersion: input.value });
    });
  });
  host.querySelectorAll('input[name="printTemplate"]').forEach((input) => {
    input.addEventListener("change", () =>
      updateUi({ printTemplate: input.value })
    );
  });

  host.querySelectorAll("[data-show]").forEach((input) => {
    input.addEventListener("change", () =>
      updateUi({ [input.dataset.show]: input.checked })
    );
  });

  const refreshPreview = () => {
    const thumb = $("#previewThumb", host);
    if (!thumb) return;
    const current = getAppState();
    thumb.innerHTML = previewSnippet(current, kind, {
      version: current.ui.printVersion,
      showCost: current.ui.showCost,
      showFrontGross: current.ui.showFrontGross,
      showBackGross: current.ui.showBackGross,
      showNetProfit: current.ui.showNetProfit,
      showTradeCredit: current.ui.showTradeCredit !== false,
      showApr: current.ui.showApr !== false,
      showFinanceCharge: current.ui.showFinanceCharge !== false,
      showAmountFinanced: current.ui.showAmountFinanced !== false,
      showDealStructure: current.ui.showDealStructure !== false,
      template: current.ui.printTemplate,
    });
    thumb.classList.add("is-pulse");
    setTimeout(() => thumb.classList.remove("is-pulse"), 450);
  };

  $("#btnClickPreview", host)?.addEventListener("click", refreshPreview);

  $("#btnPrintNext", host)?.addEventListener("click", () => {
    const ui = getAppState().ui;
    const opts = {
      version: ui.printVersion,
      showCost: ui.showCost,
      showFrontGross: ui.showFrontGross,
      showBackGross: ui.showBackGross,
      showNetProfit: ui.showNetProfit,
      showTradeCredit: ui.showTradeCredit !== false,
      showApr: ui.showApr !== false,
      showFinanceCharge: ui.showFinanceCharge !== false,
      showAmountFinanced: ui.showAmountFinanced !== false,
      showDealStructure: ui.showDealStructure !== false,
      template: ui.printTemplate,
    };
    requestPrintWithDisclaimer((printState) => {
      const app = printState || stateWithDomCustomer();
      const liveOpts = {
        ...opts,
        version: app.ui.printVersion,
        showCost: app.ui.showCost,
        showFrontGross: app.ui.showFrontGross,
        showBackGross: app.ui.showBackGross,
        showNetProfit: app.ui.showNetProfit,
        showTradeCredit: app.ui.showTradeCredit !== false,
        showApr: app.ui.showApr !== false,
        showFinanceCharge: app.ui.showFinanceCharge !== false,
        showAmountFinanced: app.ui.showAmountFinanced !== false,
        showDealStructure: app.ui.showDealStructure !== false,
        template: app.ui.printTemplate,
      };
      const ok =
        kind === "summary"
          ? openDealSummaryPdf(app, liveOpts)
          : openQuotePdf(app, liveOpts);
      setTimeout(() => {
        if (Object.keys(app._customerPatch || {}).length) {
          updateCustomer(app._customerPatch);
        }
        if (ok) closeModals();
      }, 0);
    });
  });
}

function readSelectedFormIds(host) {
  return [...host.querySelectorAll("[data-form-id]:checked")].map((el) =>
    el.getAttribute("data-form-id")
  );
}

function syncFormsModalSelection(host, state) {
  const selected = new Set(resolvedSelectedFormIds(state));
  host.querySelectorAll("[data-form-id]").forEach((input) => {
    const id = input.getAttribute("data-form-id");
    const on = selected.has(id);
    if (input.checked !== on) input.checked = on;
    input.closest("[data-form-row]")?.classList.toggle("is-selected", on);
  });
  const count = $("#formsSelectedCount", host);
  if (count) count.textContent = String(selected.size);
}

function selectedFormsFromState(state) {
  const forms = getFormsForState(state.stateCode);
  const ids = new Set(resolvedSelectedFormIds(state));
  return forms.filter((f) => ids.has(f.id));
}

function applyFormsSearch(host) {
  const q = String($("#formsSearch", host)?.value || "")
    .trim()
    .toLowerCase();
  const tokens = q.split(/\s+/).filter(Boolean);
  let visible = 0;
  host.querySelectorAll("[data-form-row]").forEach((row) => {
    const hay = row.getAttribute("data-search") || "";
    const show = !tokens.length || tokens.every((t) => hay.includes(t));
    row.hidden = !show;
    if (show) visible += 1;
  });
  const meta = $("#formsFilterMeta", host);
  if (meta) {
    meta.textContent = tokens.length
      ? `${visible} match${visible === 1 ? "" : "es"}`
      : `${visible} forms`;
  }
  const empty = $("#formsEmpty", host);
  if (empty) empty.hidden = visible > 0;
  const list = $("#formsList", host);
  if (list) list.hidden = visible === 0;
}

function wireFormsModal(host, state) {
  host.dataset.formsWired = "1";
  host.setAttribute("data-forms-wired", "1");

  host.querySelectorAll("[data-close]").forEach((el) => {
    el.addEventListener("click", (e) => {
      if (el.classList.contains("modal-backdrop") && e.target !== el) return;
      if (
        el.classList.contains("modal-backdrop") ||
        el.classList.contains("icon-btn") ||
        el.id === "btnFormsDone"
      ) {
        setSelectedFormIds(readSelectedFormIds(host));
        closeModals();
      }
    });
  });

  const persistChecks = () => {
    // Include hidden (filtered-out) rows so search never drops selections
    const ids = readSelectedFormIds(host);
    const count = $("#formsSelectedCount", host);
    if (count) count.textContent = String(ids.length);
    host.querySelectorAll("[data-form-row]").forEach((row) => {
      const input = row.querySelector("[data-form-id]");
      row.classList.toggle("is-selected", !!input?.checked);
    });
    const prev = new Set(getAppState().ui.selectedFormIds || []);
    if (ids.length === prev.size && ids.every((id) => prev.has(id))) return;
    setSelectedFormIds(ids);
  };

  host.querySelectorAll("[data-form-id]").forEach((input) => {
    input.addEventListener("change", persistChecks);
  });

  const search = $("#formsSearch", host);
  search?.addEventListener("input", () => applyFormsSearch(host));
  search?.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      search.value = "";
      applyFormsSearch(host);
      search.blur();
    }
  });
  // Focus search only if the user has not already clicked into the list
  requestAnimationFrame(() => {
    if (document.activeElement === document.body || document.activeElement === search) {
      search?.focus();
    }
  });

  $("#btnFormsAll", host)?.addEventListener("click", () => {
    const ids = getFormsForState(getAppState().stateCode).map((f) => f.id);
    setSelectedFormIds(ids);
  });

  $("#btnFormsVisible", host)?.addEventListener("click", () => {
    const visibleIds = [...host.querySelectorAll("[data-form-row]:not([hidden]) [data-form-id]")].map(
      (el) => el.getAttribute("data-form-id")
    );
    const current = new Set(readSelectedFormIds(host));
    visibleIds.forEach((id) => current.add(id));
    setSelectedFormIds([...current]);
  });

  $("#btnFormsClear", host)?.addEventListener("click", () => {
    setSelectedFormIds([]);
  });

  $("#btnFormsPriority", host)?.addEventListener("click", () => {
    setSelectedFormIds(defaultSelectedFormIds(getAppState().stateCode));
  });

  host.querySelectorAll("[data-preview-form]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.getAttribute("data-preview-form");
      const form = getFormsForState(getAppState().stateCode).find((f) => f.id === id);
      if (!form) return;
      const liveIds = readSelectedFormIds(host);
      setSelectedFormIds(liveIds);
      requestPrintWithDisclaimer((printState) => {
        const app = printState || stateWithDomCustomer();
        openFormPrint(form, app, { autoPrint: false });
        setTimeout(() => {
          if (Object.keys(app._customerPatch || {}).length) {
            updateCustomer(app._customerPatch);
          }
          setSelectedFormIds(liveIds);
        }, 0);
      });
    });
  });

  const openSelected = (autoPrint) => {
    const liveIds = readSelectedFormIds(host);
    setSelectedFormIds(liveIds);
    const preview = selectedFormsFromState({
      ...getAppState(),
      ui: { ...getAppState().ui, selectedFormIds: liveIds },
    });
    if (!preview.length) {
      alert("Select at least one form to preview or print.");
      return;
    }
    requestPrintWithDisclaimer((printState) => {
      const app = printState || stateWithDomCustomer();
      const selected = selectedFormsFromState({
        ...app,
        ui: { ...app.ui, selectedFormIds: liveIds },
      });
      if (!selected.length) {
        alert("Select at least one form to preview or print.");
        return;
      }
      openDealPacket(selected, app, { autoPrint });
      setTimeout(() => {
        if (Object.keys(app._customerPatch || {}).length) {
          updateCustomer(app._customerPatch);
        }
        setSelectedFormIds(liveIds);
      }, 0);
    });
  };

  $("#btnPreviewPacket", host)?.addEventListener("click", () =>
    openSelected(false)
  );
  $("#btnPrintPacket", host)?.addEventListener("click", () => openSelected(true));
}
